import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';
import Modal from '../../containers/modal.jsx';
import Box from '../box/box.jsx';
import {createProject, updateProject} from '../../lib/auth-api.js';
import {PROJECT_ENCODING, arrayBufferToBase64} from '../../lib/project-encoding.js';
import {describeAuthError} from '../../lib/auth-error-messages.js';
import styles from './save-project-modal.css';

/*
 * Asks for a name before writing the current project to the account.
 *
 * Naming lives here rather than in "我的作品" because the name belongs to the
 * act of saving: the dialog is the one moment where the user already knows what
 * the project is. The list stays a read-only browser with no text fields.
 *
 * Saving over an already-linked project keeps its id, so renaming here renames
 * that project instead of piling up duplicates.
 */
const messages = defineMessages({
    title: {
        id: 'gui.saveProject.title',
        defaultMessage: '保存作品',
        description: 'Title of the save-to-account dialog'
    },
    nameLabel: {
        id: 'gui.saveProject.nameLabel',
        defaultMessage: '作品名称',
        description: 'Label of the project name field'
    },
    namePlaceholder: {
        id: 'gui.saveProject.namePlaceholder',
        defaultMessage: '给作品起个名字',
        description: 'Placeholder of the project name field'
    },
    hint: {
        id: 'gui.saveProject.hint',
        defaultMessage: '作品会保存到你的账号，可在「我的作品」里打开。',
        description: 'Explains where the saved project goes'
    },
    updateHint: {
        id: 'gui.saveProject.updateHint',
        defaultMessage: '这个名字的作品已存在，保存会更新它。改成别的名字也一样更新当前作品。',
        description: 'Shown when the project is already linked to the account'
    },
    confirm: {
        id: 'gui.saveProject.confirm',
        defaultMessage: '保存',
        description: 'Button that performs the save'
    },
    cancel: {
        id: 'gui.saveProject.cancel',
        defaultMessage: '取消',
        description: 'Button that closes the dialog without saving'
    },
    saving: {
        id: 'gui.saveProject.saving',
        defaultMessage: '正在保存…',
        description: 'Shown while the save request is in flight'
    },
    nameRequired: {
        id: 'gui.saveProject.nameRequired',
        defaultMessage: '请先给作品起个名字。',
        description: 'Error shown when the name field is empty'
    },
    saveFailed: {
        id: 'gui.saveProject.saveFailed',
        defaultMessage: '保存失败：{reason}',
        description: 'Error shown when the save request fails'
    }
});

const MAX_NAME_LENGTH = 100;

class SaveProjectModal extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            name: props.defaultName || '',
            isSaving: false,
            error: null
        };
        bindAll(this, [
            'handleNameChange',
            'handleKeyDown',
            'handleSubmit'
        ]);
    }

    handleNameChange (event) {
        this.setState({name: event.target.value, error: null});
    }

    handleKeyDown (event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.handleSubmit();
        }
    }

    handleSubmit () {
        if (this.state.isSaving) return;

        const name = this.state.name.trim();
        if (!name) {
            this.setState({error: this.props.intl.formatMessage(messages.nameRequired)});
            return;
        }

        this.setState({isSaving: true, error: null});
        this.props.onSaveStart();

        // saveProjectSb3 resolves with the .sb3 archive. It is a zip, so it has
        // to travel as base64: sending it as text would mangle the binary bytes
        // and store an archive that can never be opened again.
        Promise.resolve(this.props.saveProjectSb3())
            .then(blob => blob.arrayBuffer())
            .then(buffer => {
                const payload = {
                    name: name,
                    content: arrayBufferToBase64(buffer),
                    encoding: PROJECT_ENCODING
                };
                if (this.props.projectId) {
                    return updateProject(this.props.projectId, payload)
                        .then(() => this.props.projectId);
                }
                return createProject(payload)
                    .then(project => project && project.id);
            })
            .then(projectId => {
                this.props.onSaveSuccess(projectId, name);
                this.props.onSaved();
            })
            .catch(error => {
                const reason = this.props.intl.formatMessage(describeAuthError(error));
                this.props.onSaveFailure(error);
                this.setState({
                    isSaving: false,
                    error: this.props.intl.formatMessage(messages.saveFailed, {reason: reason})
                });
            });
    }

    render () {
        const {isSaving} = this.state;
        return (
            <Modal
                className={styles.modalContent}
                id="save-project-modal"
                onRequestClose={this.props.onCancel}
            >
                <Box
                    className={styles.body}
                    id="save-project-dialog"
                >
                    <h2 className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </h2>

                    <label
                        className={styles.label}
                        htmlFor="save-project-name"
                    >
                        <FormattedMessage {...messages.nameLabel} />
                    </label>
                    <input
                        autoFocus
                        className={styles.input}
                        disabled={isSaving}
                        id="save-project-name"
                        maxLength={MAX_NAME_LENGTH}
                        placeholder={this.props.intl.formatMessage(messages.namePlaceholder)}
                        type="text"
                        value={this.state.name}
                        onChange={this.handleNameChange}
                        onKeyDown={this.handleKeyDown}
                    />

                    <p className={styles.hint}>
                        {this.props.projectId ? (
                            <FormattedMessage {...messages.updateHint} />
                        ) : (
                            <FormattedMessage {...messages.hint} />
                        )}
                    </p>

                    {this.state.error ? (
                        <div
                            className={styles.error}
                            role="alert"
                        >
                            {this.state.error}
                        </div>
                    ) : null}

                    <div className={styles.actions}>
                        <button
                            className={styles.primaryButton}
                            disabled={isSaving}
                            id="save-project-confirm"
                            type="button"
                            onClick={this.handleSubmit}
                        >
                            {isSaving ? (
                                <FormattedMessage {...messages.saving} />
                            ) : (
                                <FormattedMessage {...messages.confirm} />
                            )}
                        </button>
                        <button
                            className={styles.secondaryButton}
                            disabled={isSaving}
                            id="save-project-cancel"
                            type="button"
                            onClick={this.props.onCancel}
                        >
                            <FormattedMessage {...messages.cancel} />
                        </button>
                    </div>
                </Box>
            </Modal>
        );
    }
}

SaveProjectModal.propTypes = {
    defaultName: PropTypes.string,
    intl: intlShape.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSaveFailure: PropTypes.func,
    onSaveStart: PropTypes.func,
    onSaveSuccess: PropTypes.func,
    onSaved: PropTypes.func.isRequired,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    saveProjectSb3: PropTypes.func.isRequired
};

export default injectIntl(SaveProjectModal);
export {SaveProjectModal};
